'use client';

import gsap from 'gsap';
import CustomEase from 'gsap/CustomEase';
import SplitText from 'gsap/SplitText';
import React, { useEffect, useId, useMemo, useRef } from 'react';

gsap.registerPlugin(SplitText, CustomEase);

CustomEase.create('osmo-ease', '0.625, 0.05, 0, 1');

type SplitTextOptions = {
  text: string;
  startDelay?: number;
  onComplete?: () => void;
  type?: 'lines' | 'words' | 'chars';
  inverted?: 'dark' | 'light' | null;
};

export const useSplitTextAnimation = ({
  inverted = 'dark',
  onComplete,
  startDelay = 0,
  text,
  type = 'chars',
}: SplitTextOptions) => {
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);
  const splitInstance = useRef<SplitText | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    const node = ref.current;

    const colors =
      inverted === 'dark'
        ? ['#80ffcc', '#ecdcd8', '#2a0d5d']
        : ['#80ffcc', '#ecdcd8', '#ffffff'];

    // Wait for font metrics before splitting
    document.fonts.ready.then(() => {
      splitInstance.current = SplitText.create(node, {
        type: 'lines, words, chars',
        linesClass: 'line',
        wordsClass: 'word',
        charsClass: 'char',
        mask: 'lines',
      });

      const config = {
        lines: { duration: 0.8, stagger: 0.08 },
        words: { duration: 0.6, stagger: 0.06 },
        chars: { duration: 0.3, stagger: 0.04 },
      }[type];

      const targets =
        node &&
        node.querySelectorAll(
          type === 'lines' ? '.line' : type === 'words' ? '.word' : '.char'
        );

      node.style.visibility = 'visible';

      gsap.fromTo(
        targets,
        {
          yPercent: 0,
          opacity: 0,
          color: colors[0],
        },
        {
          keyframes: [
            {
              opacity: 1,
              color: colors[0],
              ease: 'osmo-ease',
              duration: config.duration * 0.5,
            },
            {
              opacity: 1,
              color: colors[1],
              ease: 'osmo-ease',
              duration: config.duration * 0.5,
            },
            {
              opacity: 1,

              color: colors[2],
              ease: 'osmo-ease',
              duration: config.duration * 0.5,
            },
          ],
          yPercent: 0,
          stagger: config.stagger,
          delay: startDelay,
          onComplete,
        }
      );
    });

    return () => {
      if (splitInstance.current) {
        splitInstance.current.revert();
        splitInstance.current = null;
      }
    };
  }, [text, startDelay, type, onComplete, inverted]);

  const Component = useMemo(() => {
    const SplitTextComponent: React.FC = () => {
      const lines = text.split('\n');

      return (
        <span
          ref={ref}
          key={id}
          data-split='heading'
          style={{
            display: 'inline-block',
            overflow: 'hidden',
            whiteSpace: 'pre-line',
          }}
        >
          {lines.map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </span>
      );
    };
    SplitTextComponent.displayName = 'SplitTextComponent';
    return SplitTextComponent;
  }, [id, text]);

  return { Component };
};
